import React, { useEffect, useState } from 'react';
import { getProjectCustomers } from '../../utils/api/api';
import { useParams } from 'react-router-dom';
import Layout from '../../elements/Layout';
import { FaBuilding, FaFileContract, FaMoneyBillWave } from 'react-icons/fa6';
import * as XLSX from 'xlsx';
import Loading from '../../elements/Loading';
import moment from 'moment';

const ProjectReport = () => {
    const [projectData, setProjectData] = useState(null);
    const [expandedProperty, setExpandedProperty] = useState(null);
    const { id } = useParams();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getProjectCustomers(id);
                setProjectData(response);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, [id]);

    if (!projectData) {
        return <Loading />;
    }

    const { properties } = projectData;

    const totalProperties = properties?.length;
    const totalRevenue = properties?.reduce((sum, property) => sum + property.totalAmount, 0);
    const bookedProperties = properties?.filter(p => p.status === 'booked').length;
    const underContract = properties?.filter(p => p.status === 'under_contract').length;

    const toggleProperty = (propertyId) => {
        setExpandedProperty(expandedProperty === propertyId ? null : propertyId);
    };

    const exportToExcel = () => {
        const data = properties.map(property => ({
            'Property Number': property.property_number,
            'Title Number': property.title_number,
            'Size': property.size || 'N/A',
            'Total Amount': property.totalAmount,
            'Total Paid Amount': property.totalPaidAmount,
            'Total Remaining Balance': property.totalRemainingBalance,
            'Status': property.status.replace('_', ' ').toUpperCase(),
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Properties');
        XLSX.writeFile(wb, 'ProjectReport.xlsx');
    };

    return (
        <Layout>
            <div className="min-h-screen">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-3xl font-bold text-gray-800 uppercase">{projectData.title} Report</h2>
                    <button
                        onClick={exportToExcel}
                        className="bg-primary text-white px-4 py-1.5 text-[14px] rounded-md"
                    >
                        Export to Excel
                    </button>
                </div>

                <div className="grid w-full mb-4 grid-cols-2 gap-4 mt-4 md:grid-cols-4">
                    <div className={`w-full rounded-lg p-4 flex flex-col gap-2 bg-gradient-to-r from-blue-200 to-blue-50 transform transition-transform duration-300 hover:scale-105`}>
                        <div className="flex items-center gap-2">
                            <div className="bg-blue-500 p-2 rounded-full">
                                <FaBuilding className="text-white" />
                            </div>
                            <p className="text-sm font-semibold text-primary">Total Properties</p>
                        </div>
                        <hr className="border-blue-500 " />
                        <p className="text-blue-500 font-extrabold text-end text-[25px]">{totalProperties}</p>
                    </div>
                    <div className={`w-full rounded-lg p-4 flex flex-col gap-2 bg-gradient-to-r from-blue-200 to-blue-50 transform transition-transform duration-300 hover:scale-105`}>
                        <div className="flex items-center gap-2">
                            <div className="bg-blue-500 p-2 rounded-full">
                                <FaBuilding className="text-white" />
                            </div>
                            <p className="text-sm font-semibold text-primary">Booked Properties</p>
                        </div>
                        <hr className="border-blue-500 " />
                        <p className="text-blue-500 font-extrabold text-end text-[25px]">{bookedProperties}</p>
                    </div>
                    <div className={`w-full rounded-lg p-4 flex flex-col gap-2 bg-gradient-to-r from-pink-200 to-pink-50 transform transition-transform duration-300 hover:scale-105`}>
                        <div className="flex items-center gap-2">
                            <div className="bg-pink-500 p-2 rounded-full">
                                <FaMoneyBillWave className="text-white" />
                            </div>
                            <p className="text-sm font-semibold text-primary">Total Revenue</p>
                        </div>
                        <hr className="border-pink-500 " />
                        <p className="text-pink-500 font-extrabold text-end text-[22px]">KSh {totalRevenue?.toLocaleString()}</p>
                    </div>
                    <div className={`w-full rounded-lg p-4 flex flex-col gap-2 bg-gradient-to-r from-gray-200 to-gray-50 transform transition-transform duration-300 hover:scale-105`}>
                        <div className="flex items-center gap-2">
                            <div className="bg-gray-500 p-2 rounded-full">
                                <FaFileContract className="text-white" />
                            </div>
                            <p className="text-sm font-semibold text-primary">Under Contract</p>
                        </div>
                        <hr className="border-gray-500 " />
                        <p className="text-gray-500 font-extrabold text-end text-[25px]">{underContract}</p>
                    </div>
                </div>

                <div className="bg-white shadow rounded-lg p-6 mb-8">
                    <h3 className="text-[20px] font-semibold text-black mb-4">Properties Overview</h3>
                    {properties.map(property => (
                        <div key={property.id} className="mb-6 border-b pb-4">
                            <div
                                className="flex flex-col cursor-pointer"
                                onClick={() => toggleProperty(property.id)}
                            >
                                <div className="flex w-full justify-between">
                                    <div>
                                        <p className="font-semibold text-[14px] text-gray-800">{property.property_number} - {property.title_number}</p>
                                        <p className="text-gray-500 text-[12px]">Size: {property.size || 'N/A'}</p>
                                        <p className="text-gray-700 text-[12px]">Total Amount: KSh {property.totalAmount.toLocaleString()}</p>
                                    </div>
                                    <span className={`px-4 text-[13px]  py-1 h-fit rounded-full ${property.status === 'under_contract' ? 'bg-green-100 text-green-500' : 'bg-orange-100 text-orange-600 '}`}>
                                        {property.status.replace('_', ' ').toUpperCase()}
                                    </span>
                                </div>
                                <div className="mb-4">
                                    <p className="font-semibold text-gray-800">Payment Progress</p>
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                            className="bg-blue-600 h-2.5 rounded-full"
                                            style={{ width: `${Math.round((property.totalPaidAmount / property.totalAmount) * 100)}%` }}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between mt-2">
                                        <p className="text-green-500 text-[13px]">Paid: KSh {property.totalPaidAmount.toLocaleString()}</p>
                                        <p className="text-red-500 tetx-[13px]">Remaining: KSh {property.totalRemainingBalance.toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>

                            {expandedProperty === property.id && (
                                <div className="mt-4">
                                    <div>
                                        <p className="font-semibold text-gray-800 mb-2">Contracts</p>
                                        {property.orders.map(order => (
                                            <div key={order.id} className="border rounded-lg p-4 mb-4 bg-gray-50">
                                                <p className="font-semibold text-gray-800">{order.order.customer?.first_name} {order.order.customer?.last_name}</p>
                                                <p className="text-gray-700">Total: KSh {order.order.amount}</p>
                                                <p className="text-gray-500">Payment Status:
                                                    <span className={`ml-2 px-2 py-1 rounded text-white ${order.order.payment_status === 'partially_paid' ? 'bg-yellow-500' : 'bg-red-500'}`}>
                                                        {order.order.payment_status.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                </p>

                                                {order.order.contracts && order.order.contracts.length > 0 && (
                                                    <div className="mt-4">
                                                        <p className="font-semibold text-gray-800 mb-2">Associated Contracts</p>
                                                        <ul className="space-y-2">
                                                            {order.order.contracts.map(contract => (
                                                                <li
                                                                    key={contract.id}
                                                                    className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex justify-between items-center"
                                                                >
                                                                    <div>
                                                                        <h4 className="font-semibold text-gray-800">{contract.title}</h4>
                                                                        <p className="text-gray-600 capitalize">{contract?.type?.replace('_', ' ')}</p>
                                                                        <p className="text-gray-600">{moment(contract?.createdAt).format('MMM Do YYYY')}</p>
                                                                    </div>
                                                                    <div className="flex items-center space-x-2">
                                                                        <span className="text-sm font-medium uppercase text-gray-500">{contract.status}</span>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
};

export default ProjectReport;
